import { useEffect, useRef, useState } from "react";
import { Bike, ShoppingBag, X } from "lucide-react";
import { Button, Checkbox } from "@a2z/ui";
import { useI18n, useTranslate, type TranslateFn } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";
import { billPlaceLabel } from "@/lib/invoice/place";
import type {
  InvoiceItemFulfillment,
  KitchenBoardPastPlate,
  KitchenBoardPlate,
  KitchenWorkflow,
} from "@/lib/api/types";
import { voidReasonLabelForCode } from "@/modules/pos/lib/voidReasons";
import { DoodleBadge } from "@/shared/doodles/DoodleBadge";
import type { DoodleBadgeId } from "@/shared/doodles/badges.data";
import { countCardsOutsideFold } from "../lib/laneFold";
import {
  isPlateLate,
  targetsOf,
  waitingMinutes,
  type PlateLane,
} from "../lib/lateness";
import {
  channelShape,
  identityLine,
  isOffPremise,
  showsTotalAge,
  totalAgeMinutes,
} from "../lib/channel";
import { printStartsFood, startsFromBoard } from "../lib/workflow";
import { retainedMarkOf, type RetainedMark } from "../lib/ageOff";
import { listSeparator } from "../lib/batch";

/**
 * The kitchen board's lane — the 2026-10 redesign's lanes of TICKETS, in
 * every look.
 *
 * The board's data is one row per PLATE, and so is every command; the
 * redesign draws one card per kitchen TICKET (one fire, per station). So the
 * plates in a lane are grouped by their `kitchenTicketId` here, identical
 * plates collapse into one `2×` line, and a card's button sends the existing
 * per-plate command with every plate id on the card — one command per bill,
 * through the same idempotent envelope. A ticket whose plates have moved on
 * at different speeds appears in each lane with the plates that are there,
 * which is what the kitchen is actually looking at.
 *
 * A lane may hold MORE THAN ONE state (D170, plan §4.10): the one-tap and
 * paper + pass boards draw QUEUED and PREPARING together as «في التحضير», so
 * every action is decided by each plate's own state, never by the lane:
 *  - «بدء التحضير» starts the QUEUED plates — never where the paper starts the
 *    food (a QUEUED plate there only says «لم تُطبع بعد»);
 *  - «جاهز» readies the PREPARING plates;
 *  - «تقديم» / «تسليم» (dine-in / off-premise) serves the started plates.
 * A footer label states its count when it acts on fewer plates than the card
 * shows — ticked plates, or the plates of one state («جاهز (2)»).
 *
 * The card also carries (T9, T13, T16, B5, T7 — D170):
 *  - a solid red «إعادة تحضير (N)» head on a remade ticket, a solid amber one
 *    on a late ticket (CSS), and a clock that is red only when late;
 *  - «إضافة n» for a later round of the bill, nothing on the first;
 *  - on an off-premise card, the channel's shape, `platform · ref · first
 *    name` and the total age «منذ الطلب N د» (a remade card shows it too);
 *  - the D59 version on its own line under the dish;
 *  - a voided plate struck through with «ملغى» for five minutes;
 *  - read-only «مدفوع» cards a pay-first close collapsed (T7), with «إخفاء».
 * Serving removes the card when the server answers; the «تم التقديم · #N» /
 * «تم التسليم · #N» toast with «تراجع» is the board's (`useKitchenBoard`).
 * There is no trophy, no sparks and no hold (T14).
 *
 * What the redesign's card shows and this one deliberately does not: the
 * bill's money. The board carries no prices by design — a cook has no bill.
 */

export type KitchenCommand = "startPreparing" | "markReady" | "markServed";

/** A message selector, as `t` takes it. */
type CatalogKey = Parameters<TranslateFn>[0];

interface TicketLine {
  key: string;
  plates: KitchenBoardPlate[];
  name: string;
  /** D59 — the version(s) on their own line, or null. */
  variant: string | null;
  detail: string[];
  note: string | null;
  remakeCount: number;
  remakeReason: string | null;
  remakeReasonNote: string | null;
  state: InvoiceItemFulfillment;
}

interface Ticket {
  key: string;
  ticketId: string | null;
  first: KitchenBoardPlate;
  plates: KitchenBoardPlate[];
  lines: TicketLine[];
  /** B5 — voided plates of this ticket, struck through. */
  voids: TicketLine[];
  waiting: number | null;
  /** B125/D115: the longest preparing target among the ticket's plates. */
  prepTarget: number | null;
  late: boolean;
  remakeCount: number;
  /** T7 — a read-only card of plates a close collapsed. */
  paid: boolean;
  /** Its word, by the bill's state (gate P4 #2); null on a live card. */
  mark: RetainedMark | null;
}

/**
 * Which kitchen TICKET a plate belongs to — the card it draws on. A plate
 * fired before tickets carried an id falls back to its bill and ticket
 * number, so it still lands with its siblings.
 */
export function ticketKeyOf(plate: KitchenBoardPlate): string {
  return (
    plate.kitchenTicketId ??
    `${plate.invoiceId}:${plate.ticketSequence ?? plate.id}`
  );
}

/**
 * The floor's Kitchen tab count (2026-10-03), the redesign's: the TICKETS
 * still with the kitchen — waiting or being cooked — and whether any of them
 * waits, which turns the pill red. A ticket split across two lanes is a card
 * in each, and counts once per lane, as the board shows it.
 */
export function kitchenTabCount(plates: readonly KitchenBoardPlate[]): {
  count: number;
  waiting: boolean;
} {
  const queued = new Set<string>();
  const preparing = new Set<string>();
  for (const plate of plates) {
    if (plate.fulfillmentState === "QUEUED") queued.add(ticketKeyOf(plate));
    else if (plate.fulfillmentState === "PREPARING") {
      preparing.add(ticketKeyOf(plate));
    }
  }
  return { count: queued.size + preparing.size, waiting: queued.size > 0 };
}

function lineKey(plate: KitchenBoardPlate): string {
  return JSON.stringify([
    plate.nameAr,
    plate.variantNames ?? [],
    plate.addons,
    plate.note,
    plate.substitutions ?? [],
    plate.comboComponents ?? [],
    plate.remakeCount,
    // Two remakes of one dish for different reasons are two things to fix.
    plate.remakeReason ?? null,
    plate.remakeReasonNote ?? null,
    // One lane may hold two states (one-tap, paper + pass): a line is one.
    plate.fulfillmentState,
  ]);
}

/**
 * A card's badge is its LANE's, as in the redesign: a timer while it waits, the
 * pot while it cooks, the service bell at the pass. Where the food goes is the
 * place chip beside the number ("Takeaway", "Delivery" or the table).
 */
const LANE_BADGE: Record<PlateLane, DoodleBadgeId> = {
  QUEUED: "timer",
  PREPARING: "pot",
  READY: "servicebell",
};

function plateName(plate: KitchenBoardPlate, locale: string): { name: string; variant: string | null } {
  const en = locale === "en";
  const variants = plate.variantNames ?? [];
  if (variants.length > 0 && plate.baseNameAr) {
    return {
      name: en ? (plate.baseNameEn ?? plate.baseNameAr) : plate.baseNameAr,
      variant: variants.map((one) => (en && one.nameEn ? one.nameEn : one.nameAr)).join(listSeparator(locale)),
    };
  }
  return { name: en && plate.nameEn ? plate.nameEn : plate.nameAr, variant: null };
}

function newLine(plate: KitchenBoardPlate, locale: string): TicketLine {
  const detail: string[] = [];
  for (const component of plate.comboComponents ?? []) {
    const name = locale === "en" && component.nameEn ? component.nameEn : component.nameAr;
    detail.push(component.quantity > 1 ? `${name} ×${component.quantity}` : name);
  }
  for (const swap of plate.substitutions ?? []) {
    detail.push(`− ${swap.removedNameAr}  ⟵  + ${swap.addedNameAr}`);
  }
  for (const addon of plate.addons) detail.push(`+ ${addon}`);
  return {
    key: lineKey(plate),
    plates: [],
    ...plateName(plate, locale),
    detail,
    note: plate.note,
    remakeCount: plate.remakeCount,
    remakeReason: plate.remakeReason ?? null,
    remakeReasonNote: plate.remakeReasonNote ?? null,
    state: plate.fulfillmentState,
  };
}

function addToLines(lines: TicketLine[], plate: KitchenBoardPlate, locale: string) {
  const key = lineKey(plate);
  let line = lines.find((row) => row.key === key);
  if (!line) {
    line = newLine(plate, locale);
    lines.push(line);
  }
  line.plates.push(plate);
}

function emptyTicket(plate: KitchenBoardPlate, paid = false): Ticket {
  return {
    key: ticketKeyOf(plate),
    ticketId: plate.kitchenTicketId,
    first: plate,
    plates: [],
    lines: [],
    voids: [],
    waiting: null,
    prepTarget: null,
    late: false,
    remakeCount: 0,
    paid,
    mark: null,
  };
}

/**
 * Group a lane into ticket cards, in the order the server sent them (first
 * fire, T9): the live plates, then each voided plate on its own ticket (a
 * ticket whose every plate was voided is still drawn, struck), then the
 * read-only «مدفوع» cards.
 */
function groupTickets(
  plates: readonly KitchenBoardPlate[],
  voided: readonly KitchenBoardPastPlate[],
  paid: readonly KitchenBoardPastPlate[],
  now: number,
  locale: string,
): Ticket[] {
  const tickets: Ticket[] = [];
  const byKey = new Map<string, Ticket>();
  const ticketFor = (plate: KitchenBoardPlate, isPaid = false) => {
    const key = (isPaid ? "paid:" : "") + ticketKeyOf(plate);
    let ticket = byKey.get(key);
    if (!ticket) {
      ticket = emptyTicket(plate, isPaid);
      ticket.key = key;
      byKey.set(key, ticket);
      tickets.push(ticket);
    }
    return ticket;
  };
  const measure = (ticket: Ticket, plate: KitchenBoardPlate) => {
    const minutes = waitingMinutes(plate, now);
    if (minutes !== null) ticket.waiting = Math.max(ticket.waiting ?? 0, minutes);
    // B125/D115: the bar fills toward the ticket's longest preparing target —
    // a grill ticket is measured against the grill's own time, not a salad's.
    ticket.prepTarget = Math.max(ticket.prepTarget ?? 0, targetsOf(plate).prepMinutes);
    if (isPlateLate(plate, now)) ticket.late = true;
    ticket.remakeCount = Math.max(ticket.remakeCount, plate.remakeCount);
  };
  for (const plate of plates) {
    const ticket = ticketFor(plate);
    ticket.plates.push(plate);
    measure(ticket, plate);
    addToLines(ticket.lines, plate, locale);
  }
  for (const plate of voided) addToLines(ticketFor(plate).voids, plate, locale);
  for (const plate of paid) {
    const ticket = ticketFor(plate, true);
    ticket.mark ??= retainedMarkOf(plate);
    ticket.plates.push(plate);
    ticket.remakeCount = Math.max(ticket.remakeCount, plate.remakeCount);
    addToLines(ticket.lines, plate, locale);
  }
  return tickets;
}

/**
 * An `N×` line waiting on "1 or all N?" — at most one per lane. `pick` is a
 * tap on the line: it ticks, it never moves a plate (founder, 2026-10-07).
 */
interface PendingChoice {
  ticketKey: string;
  lineKey: string;
  type: KitchenCommand | "pick";
}

/** The minutes a clock or an age shows, in the card's own words. */
export function useMinutesLabel() {
  const t = useTranslate();
  return {
    clock: (minutes: number | null) =>
      minutes === null
        ? ""
        : minutes >= 60
          ? t((m) => m.kitchen.waitingHours, { count: Math.floor(minutes / 60), minutes: minutes % 60 })
          : t((m) => m.kitchen.waitingMinutes, { count: minutes }),
    totalAge: (minutes: number | null) =>
      minutes === null
        ? ""
        : minutes >= 60
          ? t((m) => m.kitchen.totalAgeHours, { count: Math.floor(minutes / 60), minutes: minutes % 60 })
          : t((m) => m.kitchen.totalAge, { count: minutes }),
  };
}

/** The off-premise shape beside the place word (T13): a bag, or a bike. */
export function ChannelShape({ channel }: { channel: KitchenBoardPlate["channel"] }) {
  const shape = channelShape(channel);
  if (shape === null) return null;
  const Icon = shape === "bike" ? Bike : ShoppingBag;
  return (
    <Icon
      data-ui="kitchen-ticket-channel"
      data-shape={shape}
      size={14}
      strokeWidth={2}
      aria-hidden="true"
      className="inline shrink-0"
    />
  );
}

export function PaperKitchenLane({
  lane,
  label,
  emptyLabel,
  plates,
  voided = [],
  paid = [],
  workflow = "TWO_TAP_SCREEN",
  now,
  busyItemIds,
  canPrepare,
  canServe,
  isCapped = false,
  newKeys,
  onRun,
  onHidePaid,
}: {
  lane: PlateLane;
  label: string;
  emptyLabel: string;
  /** The lane's live plates — one state, or QUEUED + PREPARING in a merged lane. */
  plates: KitchenBoardPlate[];
  /** B5 — voided plates whose stamps put them in this lane, still struck. */
  voided?: KitchenBoardPastPlate[];
  /** T7 — «مدفوع» plates whose stamps put them in this lane. */
  paid?: KitchenBoardPastPlate[];
  workflow?: KitchenWorkflow;
  now: number;
  busyItemIds: ReadonlySet<string>;
  canPrepare: boolean;
  canServe: boolean;
  /**
   * The wall screen has capped the lanes to the viewport (UX-round D48): the
   * lane's body scrolls on its own and the heading says what it hides.
   */
  isCapped?: boolean;
  /** Ticket keys to mark as new for a few seconds (`useNewTicketMark`). */
  newKeys?: ReadonlySet<string>;
  /** Resolves `false` when the command failed (and was already reported). */
  onRun: (plates: KitchenBoardPlate[], type: KitchenCommand) => Promise<boolean | void>;
  /** «إخفاء» on a «مدفوع» card — device-local, nothing is sent. */
  onHidePaid?: (plateIds: string[]) => void;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const minutesLabel = useMinutesLabel();
  const [choice, setChoice] = useState<PendingChoice | null>(null);
  /*
   * Ticked plates (founder, 2026-10-07): a card's footer acts on these when
   * any of its plates are ticked, and on the whole card when none are — the
   * cashier's lane and the bill's own rule.
   */
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const targetsOf = (ticket: Ticket) => {
    const chosen = ticket.plates.filter((plate) => picked.has(plate.id));
    return chosen.length > 0 ? chosen : ticket.plates;
  };
  /**
   * A tap on a line, or on its check, TICKS it — nothing moves until a button
   * says so (founder, 2026-10-07: "it suppose only move when i click on
   * start"). A ticked line unticks; an `N×` line asks "1 or all N?" first.
   */
  const togglePick = (ticket: Ticket, line: TicketLine) => {
    if (line.plates.some((plate) => picked.has(plate.id))) {
      setPicked((current) => {
        const next = new Set(current);
        for (const plate of line.plates) next.delete(plate.id);
        return next;
      });
      return;
    }
    if (line.plates.length > 1) {
      setChoice({ ticketKey: ticket.key, lineKey: line.key, type: "pick" });
      return;
    }
    setPicked((current) => new Set(current).add(line.plates[0].id));
  };
  /** The round check is a plain checkbox: the whole line, at once. */
  const toggleWholeLine = (line: TicketLine) =>
    setPicked((current) => {
      const next = new Set(current);
      const on = !line.plates.some((plate) => current.has(plate.id));
      for (const plate of line.plates) {
        if (on) next.add(plate.id);
        else next.delete(plate.id);
      }
      return next;
    });
  const unpick = (ticket: Ticket) =>
    setPicked((current) => {
      const next = new Set(current);
      for (const plate of ticket.plates) next.delete(plate.id);
      return next;
    });

  const tickets = groupTickets(plates, voided, paid, now, locale);
  const paper = printStartsFood(workflow);
  const boardStarts = startsFromBoard(workflow);

  /**
   * HOW MANY TICKETS ARE OUT OF THE LANE'S BOX (UX-round D48) — master's lane
   * counter, measured the same way (`countCardsOutsideFold`). It is zero
   * wherever the lane is not capped: an uncapped body contains every card.
   */
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [hiddenCount, setHiddenCount] = useState(0);
  useEffect(() => {
    const node = bodyRef.current;
    if (!node) return;
    const measure = () => {
      const box = node.getBoundingClientRect();
      const cards = Array.from(node.querySelectorAll(':scope > [data-ui="kitchen-ticket"]')).map(
        (child) => child.getBoundingClientRect(),
      );
      // A layout fact, not a derived one: how many cards fit depends on the
      // heights the browser gave them, which nothing in this render knows.
      setHiddenCount(countCardsOutsideFold(box, cards));
    };
    measure();
    node.addEventListener("scroll", measure, { passive: true });
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(node);
    return () => {
      node.removeEventListener("scroll", measure);
      observer?.disconnect();
    };
  }, [plates, voided, paid, isCapped]);

  /**
   * Act on a line's plates — or, on an `N×` line, ask first. A single plate
   * moves on one tap; several never move silently (founder, 2026-10-03).
   */
  const act = (ticket: Ticket, line: TicketLine, type: KitchenCommand) => {
    if (line.plates.length === 1) {
      void onRun(line.plates, type);
      return;
    }
    setChoice({ ticketKey: ticket.key, lineKey: line.key, type });
  };

  /** A footer label, with its count when it acts on fewer plates than the card shows. */
  const scoped = (count: number, total: number, whole: CatalogKey, narrowed: CatalogKey) =>
    count < total ? t(narrowed, { count }) : t(whole);

  return (
    <section
      data-ui="kitchen-lane"
      data-lane={lane}
      data-capped={isCapped ? "" : undefined}
      className={`flex min-w-0 flex-col gap-2.5${isCapped ? " min-h-0" : ""}`}
    >
      {/* `shrink-0` and OUTSIDE the scrolling body: the heading, its count and
          what the lane hides never move (UX-round D48). */}
      <h2 data-ui="kitchen-lane-head" className="flex shrink-0 items-center gap-2">
        <span aria-hidden="true" data-ui="kitchen-lane-dot" />
        <span className="whitespace-nowrap">{label}</span>
        <span data-ui="kitchen-lane-count" data-testid={`kitchen-lane-count-${lane}`} className="tabular-nums">
          {plates.length}
        </span>
        {hiddenCount > 0 ? (
          <>
            <span className="flex-1" />
            {/* Amber and in words, in the one place that cannot scroll. */}
            <span data-ui="kitchen-lane-more" data-testid={`kitchen-lane-more-${lane}`} className="whitespace-nowrap">
              {t(
                countedKey(hiddenCount, {
                  one: (m) => m.kitchen.laneMoreOne,
                  two: (m) => m.kitchen.laneMoreTwo,
                  few: (m) => m.kitchen.laneMoreFew,
                  many: (m) => m.kitchen.laneMore,
                }),
                { count: hiddenCount },
              )}
            </span>
          </>
        ) : null}
      </h2>
      <div
        ref={bodyRef}
        data-ui="kitchen-lane-body"
        data-testid={`kitchen-lane-body-${lane}`}
        className={[
          "flex flex-col gap-2.5",
          isCapped
            ? "min-h-0 flex-1 snap-y snap-mandatory overflow-y-auto overscroll-contain"
            : "",
        ].join(" ")}
      >
        {tickets.length === 0 ? <p data-ui="kitchen-lane-empty">{emptyLabel}</p> : null}
        {tickets.map((ticket) => {
          const busy = ticket.plates.some((plate) => busyItemIds.has(plate.id));
          const offPremise = isOffPremise(ticket.first.channel);
          const live = ticket.paid ? [] : ticket.plates;
          const hasState = (state: InvoiceItemFulfillment) => live.some((plate) => plate.fulfillmentState === state);
          const canStartHere = canPrepare && boardStarts && hasState("QUEUED");
          const canReadyHere = canPrepare && hasState("PREPARING");
          const canServeHere = canServe && (hasState("PREPARING") || hasState("READY"));
          const hasActions = canStartHere || canReadyHere || canServeHere;
          const targets = ticket.paid ? [] : targetsOf(ticket);
          const startTargets = boardStarts ? targets.filter((plate) => plate.fulfillmentState === "QUEUED") : [];
          const readyTargets = targets.filter((plate) => plate.fulfillmentState === "PREPARING");
          const serveTargets = targets.filter(
            (plate) => plate.fulfillmentState === "PREPARING" || plate.fulfillmentState === "READY",
          );
          const unprinted = paper && hasState("QUEUED");
          const identity = offPremise ? identityLine(ticket.first) : null;
          const age = showsTotalAge(ticket.plates.length > 0 ? ticket.plates : [ticket.first])
            ? totalAgeMinutes(ticket.plates.length > 0 ? ticket.plates : [ticket.first], now)
            : null;
          const addOn = (ticket.first.ticketSequence ?? 1) - 1;
          const place = billPlaceLabel(
            { tableIds: ticket.first.tableNames, channel: ticket.first.channel },
            (name) => name,
            t,
          );
          return (
            <article
              key={ticket.key}
              data-ui="kitchen-ticket"
              data-late={ticket.late ? "true" : undefined}
              data-remake={ticket.remakeCount > 0 ? "true" : undefined}
              data-paid={ticket.paid ? "" : undefined}
              data-new={newKeys?.has(ticket.key) ? "" : undefined}
              data-channel={ticket.first.channel}
              data-testid={`kitchen-ticket-${ticket.key}`}
              data-ticket-key={ticket.key}
              data-lane={lane}
              // `shrink-0`: in a capped lane the BODY scrolls; a card with
              // `overflow-hidden` would otherwise be squashed to fit it.
              className="relative flex shrink-0 snap-start flex-col overflow-hidden"
            >
              <header data-ui="kitchen-ticket-head" className="flex items-center gap-1.5">
                <span className="relative shrink-0">
                  <DoodleBadge id={LANE_BADGE[lane]} size={28} animate={false} />
                  {/* The lane's state as a dot on the badge: red waiting, blue
                      on the stove, green at the pass. Decoration only — the
                      lane's heading says the same in words. */}
                  <span aria-hidden="true" data-ui="kitchen-ticket-state" className="absolute flex">
                    <span data-ui="kitchen-ticket-state-dot" />
                  </span>
                </span>
                <span data-ui="kitchen-ticket-number" className="whitespace-nowrap tabular-nums">
                  #{ticket.first.dailySequence}
                </span>
                <span data-ui="kitchen-ticket-place" className="flex items-center gap-1 truncate">
                  {/* Takeaway / delivery: the word AND a shape (T13). */}
                  <ChannelShape channel={ticket.first.channel} />
                  <span className="truncate">{place}</span>
                </span>
                {/* A later round of the bill (T14): «إضافة 1» for its second
                    fire; nothing on the first. */}
                {addOn > 0 ? (
                  <span
                    data-ui="kitchen-ticket-round"
                    data-testid={`kitchen-ticket-round-${ticket.key}`}
                    className="whitespace-nowrap tabular-nums"
                  >
                    {t((m) => m.kitchen.addOnRound, { round: addOn })}
                  </span>
                ) : null}
                {ticket.remakeCount > 0 ? (
                  <span
                    data-ui="kitchen-ticket-remake"
                    data-testid={`kitchen-ticket-remake-${ticket.key}`}
                    className="whitespace-nowrap tabular-nums"
                  >
                    {t((m) => m.kitchen.remakeBadge, { count: ticket.remakeCount })}
                  </span>
                ) : null}
                <span className="flex-1" />
                {ticket.late ? (
                  <span data-ui="kitchen-ticket-late" data-testid={`kitchen-ticket-late-${ticket.key}`}>
                    {lane === "READY" ? t((m) => m.kitchen.lateBadgeReady) : t((m) => m.kitchen.lateBadge)}
                  </span>
                ) : null}
                {unprinted ? (
                  <span data-ui="kitchen-ticket-unprinted" data-testid={`kitchen-ticket-unprinted-${ticket.key}`}>
                    {t((m) => m.kitchen.unprinted)}
                  </span>
                ) : null}
                {ticket.paid ? (
                  <span
                    data-ui="kitchen-ticket-paid"
                    data-mark={ticket.mark ?? undefined}
                    data-testid={`kitchen-ticket-paid-${ticket.key}`}
                  >
                    {ticket.mark === "paid" ? t((m) => m.kitchen.paidMark) : t((m) => m.kitchen.billClosedMark)}
                  </span>
                ) : null}
                {/* Red only when late (T16): a fresh waiting ticket is not an
                    emergency, and a clock that is always red means nothing. */}
                <span
                  data-ui="kitchen-ticket-time"
                  data-alert={ticket.late ? "" : undefined}
                  className="whitespace-nowrap tabular-nums"
                >
                  {minutesLabel.clock(ticket.waiting)}
                </span>
              </header>

              {identity || age !== null ? (
                <div
                  data-ui="kitchen-ticket-identity"
                  data-testid={`kitchen-ticket-identity-${ticket.key}`}
                  className="flex flex-wrap items-center justify-between gap-x-3"
                >
                  {identity ? <bdi data-ui="kitchen-ticket-who">{identity}</bdi> : null}
                  {age !== null ? (
                    <span data-ui="kitchen-ticket-age" className="whitespace-nowrap tabular-nums">
                      {minutesLabel.totalAge(age)}
                    </span>
                  ) : null}
                </div>
              ) : null}

              <div data-ui="kitchen-ticket-lines" className="flex flex-col">
                {ticket.lines.map((line) => {
                  const lineBusy = line.plates.some((plate) => busyItemIds.has(plate.id));
                  const first = line.plates[0];
                  const pickedCount = line.plates.filter((plate) => picked.has(plate.id)).length;
                  const lineHasActions = hasActions && !ticket.paid;
                  /* The per-plate step master's board offered (Start one dish,
                     send the fries to the pass while the burger cooks) — on a
                     ticket of several plates only; a single plate's card
                     button IS its step. */
                  const lineCommand: KitchenCommand | null =
                    canPrepare && !ticket.paid && ticket.plates.length > 1
                      ? line.state === "QUEUED" && boardStarts
                        ? "startPreparing"
                        : line.state === "PREPARING"
                          ? "markReady"
                          : null
                      : null;
                  const choosing =
                    choice !== null &&
                    choice.ticketKey === ticket.key &&
                    choice.lineKey === line.key &&
                    line.plates.length > 1
                      ? choice
                      : null;
                  const body = (
                    <>
                      <span data-ui="kitchen-line-qty" className="shrink-0 tabular-nums">
                        {/* "1/3×": one of three ticked. */}
                        {pickedCount > 0 && pickedCount < line.plates.length
                          ? `${pickedCount}/${line.plates.length}×`
                          : `${line.plates.length}×`}
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span data-ui="kitchen-line-name"><bdi>{line.name}</bdi></span>
                        {line.variant ? (
                          <span data-ui="kitchen-line-variant" data-testid={`kitchen-line-variant-${first.id}`}>
                            <bdi>{line.variant}</bdi>
                          </span>
                        ) : null}
                        {/* B117/Q4 + B118 — WHY the table sent it back, on the
                            line the cook is reading: instructions, not blame. */}
                        {line.remakeReason || line.remakeReasonNote ? (
                          <span
                            data-ui="kitchen-line-remake-reason"
                            data-testid={`kitchen-line-remake-reason-${first.id}`}
                            dir="auto"
                          >
                            {[
                              line.remakeReason
                                ? t((m) => m.kitchen.remakeReasonLine, {
                                    reason: voidReasonLabelForCode(t, line.remakeReason as string, "remake"),
                                  })
                                : null,
                              line.remakeReasonNote,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        ) : null}
                        {line.note ? <span data-ui="kitchen-line-note">{"★ "}<bdi>{line.note}</bdi></span> : null}
                        {line.detail.length > 0 ? (
                          <span data-ui="kitchen-line-detail"><bdi>{line.detail.join("  ·  ")}</bdi></span>
                        ) : null}
                      </span>
                      {line.remakeCount > 0 ? (
                        <span data-ui="kitchen-line-remake" className="shrink-0">
                          {t((m) => m.kitchen.remakeBadge, { count: line.remakeCount })}
                        </span>
                      ) : null}
                    </>
                  );
                  return (
                    <div key={line.key} className="flex flex-col">
                      <div
                        data-ui="kitchen-line-row"
                        data-remake={line.remakeCount > 0 ? "true" : undefined}
                        className="flex items-center"
                      >
                        {lineHasActions ? (
                          <span data-ui="kitchen-line-pick" className="flex shrink-0 items-center">
                            <Checkbox
                              size="md"
                              checked={pickedCount > 0}
                              disabled={lineBusy}
                              aria-label={t((m) => m.pos.pickItem)}
                              data-testid={`kitchen-line-pick-${first.id}`}
                              onChange={() => {
                                setChoice(null);
                                toggleWholeLine(line);
                              }}
                            />
                          </span>
                        ) : null}
                        {lineHasActions ? (
                          <button
                            type="button"
                            data-ui="kitchen-line"
                            data-testid={`kitchen-line-${first.id}`}
                            aria-pressed={pickedCount > 0}
                            aria-expanded={line.plates.length > 1 ? choosing?.type === "pick" : undefined}
                            disabled={lineBusy}
                            onClick={() => togglePick(ticket, line)}
                            className="flex w-full items-center gap-2 text-start"
                          >
                            {body}
                          </button>
                        ) : (
                          <div data-ui="kitchen-line" data-testid={`kitchen-line-${first.id}`} className="flex items-center gap-2">
                            {body}
                          </div>
                        )}
                        {lineCommand ? (
                          <button
                            type="button"
                            data-ui="kitchen-line-action"
                            data-kind={lineCommand === "startPreparing" ? "start" : "ready"}
                            data-testid={`kitchen-line-${lineCommand === "startPreparing" ? "start" : "ready"}-${first.id}`}
                            aria-label={`${t((m) =>
                              lineCommand === "startPreparing" ? m.kitchen.actionStart : m.kitchen.actionReady,
                            )}: ${line.plates.length}× ${line.name}`}
                            aria-expanded={line.plates.length > 1 ? choosing?.type === lineCommand : undefined}
                            disabled={lineBusy}
                            onClick={() => act(ticket, line, lineCommand)}
                            className="shrink-0 whitespace-nowrap"
                          >
                            {t((m) =>
                              lineCommand === "startPreparing" ? m.kitchen.actionStart : m.kitchen.actionReady,
                            )}
                          </button>
                        ) : null}
                      </div>
                      {choosing ? (
                        <LineChoice
                          kind={
                            choosing.type === "pick"
                              ? "pick"
                              : choosing.type === "startPreparing"
                                ? "start"
                                : choosing.type === "markReady"
                                  ? "ready"
                                  : "serve"
                          }
                          {...(choosing.type === "pick"
                            ? pickChoiceLabels(t, line.plates.length)
                            : choiceLabels(t, choosing.type, line.plates.length))}
                          name={line.name}
                          busy={lineBusy}
                          testId={`kitchen-line-choice-${first.id}`}
                          onPick={(all) => {
                            setChoice(null);
                            const chosen = all ? line.plates : [first];
                            if (choosing.type === "pick") {
                              setPicked((current) => {
                                const next = new Set(current);
                                for (const plate of chosen) next.add(plate.id);
                                return next;
                              });
                              return;
                            }
                            void onRun(chosen, choosing.type);
                          }}
                          onCancel={() => setChoice(null)}
                        />
                      ) : null}
                    </div>
                  );
                })}
                {/* B5 — voided after firing: struck through with «ملغى» for
                    five minutes, so the cook stops making it. Never actionable. */}
                {ticket.voids.map((line) => (
                  <div
                    key={`void:${line.key}`}
                    data-ui="kitchen-line-void"
                    data-testid={`kitchen-line-void-${line.plates[0].id}`}
                    className="flex items-center gap-2"
                  >
                    <span data-ui="kitchen-line-qty" className="shrink-0 tabular-nums">
                      {line.plates.length}×
                    </span>
                    <s data-ui="kitchen-line-void-name" className="min-w-0 flex-1">
                      <bdi>{line.name}</bdi>
                      {line.variant ? <> — <bdi>{line.variant}</bdi></> : null}
                    </s>
                    <span data-ui="kitchen-line-void-mark" className="shrink-0">
                      {t((m) => m.kitchen.voidMark)}
                    </span>
                  </div>
                ))}
              </div>

              {/* On the stove: the redesign's striped bar, filling toward the
                  ticket's own preparing target (B125/D115) — the same clock
                  that turns the card amber. */}
              {lane === "PREPARING" && !ticket.paid && ticket.waiting !== null && ticket.prepTarget ? (
                <div data-ui="kitchen-ticket-progress" aria-hidden="true">
                  <div
                    data-ui="kitchen-ticket-progress-bar"
                    data-testid={`kitchen-ticket-progress-${ticket.key}`}
                    style={{
                      width: `${Math.min(95, Math.max(8, (ticket.waiting / ticket.prepTarget) * 100))}%`,
                    }}
                  />
                </div>
              ) : null}

              {/* No "Print chit" (founder, 2026-10-07): the chit prints itself
                  when the plates are sent. One action spans the card; two or
                  three share it. */}
              {ticket.paid && onHidePaid ? (
                <footer data-ui="kitchen-ticket-foot" className="flex flex-wrap items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`kitchen-ticket-hide-${ticket.key}`}
                    onClick={() => onHidePaid(ticket.plates.map((plate) => plate.id))}
                  >
                    {t((m) => m.kitchen.hide)}
                  </Button>
                </footer>
              ) : null}
              {hasActions ? (
                <footer data-ui="kitchen-ticket-foot" className="flex flex-wrap items-center justify-end gap-2">
                  {canStartHere ? (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={busy || startTargets.length === 0}
                      onClick={() => {
                        void onRun(startTargets, "startPreparing");
                        unpick(ticket);
                      }}
                      data-testid={`kitchen-ticket-start-${ticket.key}`}
                    >
                      {scoped(startTargets.length, live.length, (m) => m.kitchen.actionStart, (m) => m.kitchen.actionStartScoped)}
                    </Button>
                  ) : null}
                  {canServeHere ? (
                    <Button
                      variant={lane === "READY" ? "primary" : "outline"}
                      size="sm"
                      data-kind={lane === "READY" ? "serve" : undefined}
                      disabled={busy || serveTargets.length === 0}
                      onClick={() => {
                        void onRun(serveTargets, "markServed");
                        unpick(ticket);
                      }}
                      data-testid={`kitchen-ticket-served-${ticket.key}`}
                    >
                      {offPremise
                        ? scoped(serveTargets.length, live.length, (m) => m.kitchen.actionHandOver, (m) => m.kitchen.actionHandOverScoped)
                        : scoped(serveTargets.length, live.length, (m) => m.kitchen.actionServed, (m) => m.kitchen.actionServedScoped)}
                    </Button>
                  ) : null}
                  {canReadyHere ? (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={busy || readyTargets.length === 0}
                      onClick={() => {
                        void onRun(readyTargets, "markReady");
                        unpick(ticket);
                      }}
                      data-testid={`kitchen-ticket-ready-${ticket.key}`}
                    >
                      {scoped(readyTargets.length, live.length, (m) => m.kitchen.actionReady, (m) => m.kitchen.actionReadyScoped)}
                    </Button>
                  ) : null}
                </footer>
              ) : null}
            </article>
          );
        })}
      </div>
    </section>
  );
}

/** "Select 1" / "Select all N" — ticking an `N×` line (founder, 2026-10-07). */
export function pickChoiceLabels(t: TranslateFn, count: number): { single: string; all: string } {
  return {
    single: t((m) => m.paperKitchen.pickSingle),
    all: t(
      countedKey(count, {
        one: (m) => m.paperKitchen.pickSingle,
        two: (m) => m.paperKitchen.pickAllTwo,
        few: (m) => m.paperKitchen.pickAllFew,
        many: (m) => m.paperKitchen.pickAll,
      }),
      { count },
    ),
  };
}

/** The two words of "1 or all N?" for one command. */
function choiceLabels(
  t: TranslateFn,
  type: KitchenCommand,
  count: number,
): { single: string; all: string } {
  switch (type) {
    case "startPreparing":
      return {
        single: t((m) => m.paperKitchen.startSingle),
        all: t(
          countedKey(count, {
            one: (m) => m.paperKitchen.startSingle,
            two: (m) => m.paperKitchen.startAllTwo,
            few: (m) => m.paperKitchen.startAllFew,
            many: (m) => m.paperKitchen.startAll,
          }),
          { count },
        ),
      };
    case "markReady":
      return {
        single: t((m) => m.paperKitchen.readySingle),
        all: t(
          countedKey(count, {
            one: (m) => m.paperKitchen.readySingle,
            two: (m) => m.paperKitchen.readyAllTwo,
            few: (m) => m.paperKitchen.readyAllFew,
            many: (m) => m.paperKitchen.readyAll,
          }),
          { count },
        ),
      };
    case "markServed":
      return {
        single: t((m) => m.paperKitchen.serveSingle),
        all: t(
          countedKey(count, {
            one: (m) => m.paperKitchen.serveSingle,
            two: (m) => m.paperKitchen.serveAllTwo,
            few: (m) => m.paperKitchen.serveAllFew,
            many: (m) => m.paperKitchen.serveAll,
          }),
          { count },
        ),
      };
  }
}

/**
 * "1 or all N?" under an `N×` line — inline, in the ticket, so the answer is
 * one more tap where the question was asked. The first choice takes focus,
 * and Escape or the × backs out without sending anything.
 */
export function LineChoice({
  kind,
  single,
  all,
  name,
  busy,
  testId,
  onPick,
  onCancel,
}: {
  /** The step it takes, for its colour: the kitchen's three, or the cashier's two. */
  kind: "start" | "ready" | "serve" | "pick";
  single: string;
  all: string;
  name: string;
  busy: boolean;
  testId: string;
  onPick: (all: boolean) => void;
  onCancel: () => void;
}) {
  const t = useTranslate();
  const firstRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    firstRef.current?.focus();
  }, []);
  return (
    <div
      role="group"
      aria-label={t((m) => m.paperKitchen.choiceLabel, { name })}
      data-ui="kitchen-line-choice"
      data-kind={kind}
      data-testid={testId}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          onCancel();
        }
      }}
      className="flex flex-wrap items-center gap-1.5"
    >
      <button
        ref={firstRef}
        type="button"
        data-ui="kitchen-line-choice-option"
        data-testid={`${testId}-one`}
        disabled={busy}
        onClick={() => onPick(false)}
        className="whitespace-nowrap"
      >
        {single}
      </button>
      <button
        type="button"
        data-ui="kitchen-line-choice-option"
        data-all=""
        data-testid={`${testId}-all`}
        disabled={busy}
        onClick={() => onPick(true)}
        className="whitespace-nowrap"
      >
        {all}
      </button>
      <button
        type="button"
        data-ui="kitchen-line-choice-cancel"
        data-testid={`${testId}-cancel`}
        aria-label={t((m) => m.common.cancel)}
        title={t((m) => m.common.cancel)}
        onClick={onCancel}
        className="flex shrink-0 items-center justify-center"
      >
        <X size={14} strokeWidth={2} aria-hidden="true" />
      </button>
    </div>
  );
}
